import React from 'react';
import { ShoppingCart, Trash2, Plus, Minus, Send, RotateCcw, Package } from 'lucide-react';

function CartSidebar({ cart, onUpdateQuantity, onRemove, onCheckout, selectedProviderName, onChangeProvider }) {
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  function calculateTotal() {
    return cart.reduce((sum, item) => {
      const price = item.dropship_price || item.bulk_price || item.price || 0;
      return sum + (price * item.quantity);
    }, 0);
  }

  return (
    <div className="w-80 sticky top-24 h-fit max-h-[calc(100vh-7rem)] flex flex-col">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full">
        {/* Header */}
        <div className="bg-brand-primary text-white p-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="font-bold text-lg font-montserrat">Carrito</h2>
            {totalItems > 0 && (
              <span className="ml-auto bg-white text-brand-primary px-2.5 py-0.5 rounded-full text-sm font-bold font-montserrat">
                {totalItems}
              </span>
            )}
          </div>
          
          {/* NEW: Provider Badge in Cart Header */}
          {selectedProviderName && (
            <div className="mt-2 pt-2 border-t border-white/20">
              <div className="flex items-center gap-2 text-xs">
                <Package className="w-3.5 h-3.5" />
                <span className="font-montserrat opacity-90">
                  Proveedor: <span className="font-semibold">{selectedProviderName}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <ShoppingCart className="w-16 h-16 text-slate-300 mb-3" />
              <p className="text-slate-500 font-montserrat text-sm">
                Tu carrito está vacío
              </p>
              <p className="text-slate-400 font-montserrat text-xs mt-1">
                Agrega productos para continuar
              </p>
            </div>
          ) : (
            cart.map(item => (
              <CartItem
                key={item.sku}
                item={item}
                onUpdateQuantity={onUpdateQuantity}
                onRemove={onRemove}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-slate-200 p-4 bg-slate-50 space-y-3">
            {/* NEW: Change Provider Button */}
            {onChangeProvider && (
              <button
                onClick={onChangeProvider}
                className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg transition-all font-montserrat font-medium text-sm flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Cambiar Proveedor
              </button>
            )}
            
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-700 font-montserrat">Total:</span>
              <span className="text-xl font-bold text-brand-primary font-montserrat">
                S/ {calculateTotal().toFixed(2)}
              </span>
            </div>

            <button
              onClick={onCheckout}
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white py-3 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              <Send className="w-5 h-5" />
              Enviar Pedido
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CartItem({ item, onUpdateQuantity, onRemove }) {
  const price = item.dropship_price || item.bulk_price || item.price || 0;
  const subtotal = price * item.quantity;

  function handleIncrement() {
    onUpdateQuantity(item.sku, item.quantity + 1);
  }

  function handleDecrement() {
    if (item.quantity > 1) {
      onUpdateQuantity(item.sku, item.quantity - 1);
    }
  }

  return (
    <div className="bg-slate-50 rounded-lg p-3 relative group">
      <button
        onClick={() => onRemove(item.sku)}
        className="absolute top-2 right-2 p-1 hover:bg-red-100 rounded-md transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Eliminar del carrito"
      >
        <Trash2 className="w-4 h-4 text-red-500" />
      </button>

      <div className="flex gap-3">
        <img
          src={item.primary_image || '/placeholder-image.png'}
          alt={item.title}
          className="w-16 h-16 object-cover rounded-md bg-white"
          onError={(e) => {
            e.target.src = '/placeholder-image.png';
          }}
        />

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-slate-800 font-montserrat line-clamp-2 mb-1">
            {item.title}
          </h4>

          <p className="text-xs text-slate-500 font-montserrat mb-2">
            S/ {price.toFixed(2)} c/u
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-white rounded-md border border-slate-300">
              <button
                onClick={handleDecrement}
                className="p-1.5 hover:bg-slate-100 transition-colors rounded-l-md"
                aria-label="Disminuir cantidad"
              >
                <Minus className="w-3.5 h-3.5 text-slate-600" />
              </button>

              <span className="text-sm font-semibold text-slate-800 font-montserrat min-w-[2rem] text-center">
                {item.quantity}
              </span>

              <button
                onClick={handleIncrement}
                className="p-1.5 hover:bg-slate-100 transition-colors rounded-r-md"
                aria-label="Aumentar cantidad"
              >
                <Plus className="w-3.5 h-3.5 text-slate-600" />
              </button>
            </div>

            <span className="text-sm font-bold text-brand-primary font-montserrat">
              S/ {subtotal.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CartSidebar;